import { useCallback } from 'react';
import { cn } from '@rocco/utils/cn';
import { useAuth } from '~/modules/root/context/AuthContext';
import type { LoginFlow } from '~/modules/user/hook/use-login-flow';

interface SpecifierLoginPromptProps {
    /** The professional's display name. */
    professionalName: string;
    /** The item description appended to the prompt copy. */
    itemDescription?: string;
    /** Optional additional CSS classes. */
    className?: string;
    /**
     * Show a bottom divider line. Defaults to `true`.
     * Disable when the next section renders its own top border.
     */
    showBottomBorder?: boolean;
}

/**
 * A prompt displayed to logged-out users when a professional has set their
 * enquiry audience to "Specifiers only". Provides links to sign up or log in.
 */
export const SpecifierLoginPrompt = ({
    professionalName,
    itemDescription = 'this product',
    className,
    showBottomBorder = true,
}: SpecifierLoginPromptProps) => {
    const { showAuthModal } = useAuth();

    const handleSignUp = useCallback(() => {
        showAuthModal({
            authSource: 'enquiry',
            flow: 'signup' as LoginFlow,
        });
    }, [showAuthModal]);

    const handleLogIn = useCallback(() => {
        showAuthModal({
            authSource: 'enquiry',
            flow: 'login' as LoginFlow,
        });
    }, [showAuthModal]);

    return (
        <div
            className={cn(
                'py-6',
                showBottomBorder && 'mb-px border-b border-black',
                className,
            )}
            data-testid="specifier-login-prompt"
        >
            <div className="border-charcoal-50 bg-warmWhite-25 rounded-md border p-5 shadow-sm">
                <p className="text-charcoal-250 text-base leading-snug">
                    Are you a specifier?{' '}
                    <button
                        type="button"
                        onClick={handleSignUp}
                        className="hover:text-charcoal-100 font-medium text-black underline"
                    >
                        Sign up
                    </button>{' '}
                    /{' '}
                    <button
                        type="button"
                        onClick={handleLogIn}
                        className="hover:text-charcoal-100 font-medium text-black underline"
                    >
                        log in
                    </button>{' '}
                    to enquire directly with {professionalName} about{' '}
                    {itemDescription}.
                </p>
            </div>
        </div>
    );
};
